import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FiCheck, FiEdit2, FiPlus, FiUploadCloud } from "react-icons/fi";
import FormalityHeader from "../../../components/header/FormalityHeader";
import GuideSection from "../../../components/guide/GuideSection";
import { visualIdentityGuide } from "../../../components/guide/guideContent";
import {
  PriceBlock,
  QuantityStepper,
  SummaryBar,
  showError,
} from "../../../components/options/OptionParts";
import {
  amountFor,
  identityServices,
  quantityLabel,
  unitHint,
} from "../../../components/options/identityServices";
import { addToCart } from "../../../redux/actions/CartActions";
import {
  createVisualIdentityRequest,
  uploadVisualIdentityLogo,
} from "../../../redux/actions/FormalityActions";
import serviceCode from "../../../assets/data/serviceCode.json";
import { formatPrice } from "../../../utils/formatPrice";
import { clampQuantity } from "../../../utils/creationFlow";
import "../../styles/options.css";
import "../../styles/flow-form.css";

const MAX_LOGO_SIZE = 5 * 1024 * 1024;

function VisualIdentity() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const previous = cart?.visualIdentity;

  // Previous answers are restored when the user comes back to this step
  const [selection, setSelection] = useState(() =>
    Object.fromEntries(
      identityServices.map((option) => {
        const item = previous?.items?.find((i) => i.key === option.key);

        return [
          option.key,
          {
            selected: Boolean(item),
            quantity: option.rule
              ? clampQuantity(option.key, item?.quantity)
              : undefined,
          },
        ];
      }),
    ),
  );
  const [businessName, setBusinessName] = useState(
    previous?.businessName || "",
  );
  const [details, setDetails] = useState(previous?.details || "");
  const [logoFile, setLogoFile] = useState(null);
  const [touched, setTouched] = useState(false);
  // The supports are chosen first, then the details are filled in
  const [step, setStep] = useState("choice");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const goTo = (next) => setStep(next);

  const toggle = (key) =>
    setSelection((prev) => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key].selected },
    }));

  const setQuantity = (key, quantity) =>
    setSelection((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity, selected: true },
    }));

  const selected = identityServices.filter((o) => selection[o.key].selected);
  const total = selected.reduce(
    (sum, option) => sum + amountFor(option, selection[option.key]),
    0,
  );
  const nameError =
    businessName.trim().length < 2 ? "Indiquez le nom à faire figurer." : null;
  const isValid = selected.length > 0 && !nameError;

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];

    if (file && file.size > MAX_LOGO_SIZE) {
      showError(Swal, "Le fichier ne doit pas dépasser 5 Mo.");
      event.target.value = "";
      return;
    }

    setLogoFile(file || null);
  };

  const handleContinue = async () => {
    if (step === "choice") {
      goTo("details");
      return;
    }

    setTouched(true);
    if (!isValid) return;

    setSubmitting(true);

    const services = selected.map((option) => ({
      code: serviceCode[option.key],
      billing: "lifetime",
      ...(option.rule ? { quantity: selection[option.key].quantity } : {}),
    }));

    await dispatch(
      createVisualIdentityRequest(
        user.token,
        {
          businessName: businessName.trim(),
          details: details.trim() || undefined,
          services,
          country: user.country,
        },
        handleCreatedWithLogo,
      ),
    );
  };

  const handleCreated = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({
        price: total,
        visualIdentity: {
          businessName: businessName.trim(),
          details: details.trim(),
          items: selected.map((option) => ({
            key: option.key,
            title: option.title,
            quantity: selection[option.key].quantity,
            amount: amountFor(option, selection[option.key]),
          })),
        },
      }),
    );

    setSubmitting(false);
    navigate("/formality-payment");
  };

  // The existing logo is sent once the request exists
  const handleCreatedWithLogo = (response) => {
    if (!response.success || !logoFile) {
      handleCreated(response);
      return;
    }

    const formData = new FormData();
    formData.append("existingLogo", logoFile);

    dispatch(
      uploadVisualIdentityLogo(
        user.token,
        response.data?.reference,
        formData,
        (upload) => handleCreated(upload.success ? response : upload),
      ),
    );
  };

  const label =
    selected.length === 0
      ? "Choisissez au moins un support"
      : `${selected.length} support${selected.length > 1 ? "s" : ""}`;

  return (
    <div className="options-page flow-fields">
      <FormalityHeader from="visualIdentity" />

      <div className="container options-container">
        <header className="options-header">
          {step === "choice" ? (
            <>
              <h1 className="options-title">
                Votre <span>identité visuelle</span>
              </h1>
              <p className="options-lead">
                Logo, cartes de visite, plaquettes et cachets conçus par nos
                graphistes. Délai : environ 1 semaine.
              </p>
            </>
          ) : (
            <>
              <h1 className="options-title">
                Vos <span>informations</span>
              </h1>
              <p className="options-lead">
                Elles permettent à nos graphistes de préparer vos supports.
              </p>
            </>
          )}
        </header>

        {step === "choice" ? (
          <>
            <div className="options-grid">
              {identityServices.map((option) => {
                const state = selection[option.key];
                const Icon = option.icon;

                return (
                  <article
                    key={option.key}
                    className={`option-card ${state.selected ? "is-selected" : ""}`}
                  >
                    <div className="option-card-top">
                      <span className="option-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <button
                        type="button"
                        className="option-toggle"
                        aria-pressed={state.selected}
                        onClick={() => toggle(option.key)}
                      >
                        {state.selected ? (
                          <>
                            <FiCheck aria-hidden="true" /> Ajouté
                          </>
                        ) : (
                          <>
                            <FiPlus aria-hidden="true" /> Ajouter
                          </>
                        )}
                      </button>
                    </div>

                    <h2 className="option-title">{option.title}</h2>
                    <p className="option-summary">{option.summary}</p>

                    <div className="option-card-pricing">
                      {option.rule ? (
                        <>
                          <QuantityStepper
                            value={state.quantity}
                            min={option.rule.min}
                            step={option.rule.step}
                            unitLabel={quantityLabel(option, state.quantity)}
                            onChange={(quantity) =>
                              setQuantity(option.key, quantity)
                            }
                          />
                          <p className="option-price">
                            <strong>
                              {formatPrice(amountFor(option, state))}
                            </strong>
                          </p>
                          <p className="option-unit">{unitHint(option)}</p>
                        </>
                      ) : (
                        <PriceBlock prices={option.prices} />
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="seat-panel identity-panel identity-recap">
              <div className="identity-recap-head">
                <span className="flow-label">Vos supports</span>
                <button
                  type="button"
                  className="identity-recap-edit"
                  onClick={() => goTo("choice")}
                >
                  <FiEdit2 aria-hidden="true" /> Modifier mes supports
                </button>
              </div>
              <ul className="identity-recap-list">
                {selected.map((option) => (
                  <li key={option.key}>
                    <span>
                      {option.title}
                      {option.rule &&
                        ` · ${selection[option.key].quantity} ${quantityLabel(
                          option,
                          selection[option.key].quantity,
                        )}`}
                    </span>
                    <strong>
                      {formatPrice(amountFor(option, selection[option.key]))}
                    </strong>
                  </li>
                ))}
              </ul>
            </div>

            <div className="seat-panel identity-panel">
              <div className="flow-field">
                <label htmlFor="identity-name" className="flow-label">
                  Nom à faire figurer
                </label>
                <input
                  id="identity-name"
                  type="text"
                  className={`flow-input ${
                    touched && nameError ? "is-invalid" : ""
                  }`}
                  placeholder="Ex. : Kribi Digital"
                  value={businessName}
                  onChange={(event) => setBusinessName(event.target.value)}
                  autoComplete="organization"
                />
                {touched && nameError && (
                  <p className="flow-error">{nameError}</p>
                )}
              </div>

              <div className="flow-field">
                <label htmlFor="identity-details" className="flow-label">
                  Précisions <small>(facultatif)</small>
                </label>
                <textarea
                  id="identity-details"
                  className="flow-input flow-textarea"
                  rows={4}
                  maxLength={2000}
                  placeholder="Coordonnées pour les cartes, couleurs souhaitées, textes de la plaquette…"
                  value={details}
                  onChange={(event) => setDetails(event.target.value)}
                />
              </div>

              <div className="flow-field">
                <span className="flow-label">
                  Logo existant <small>(facultatif)</small>
                </span>
                <label htmlFor="identity-logo" className="flow-file">
                  <FiUploadCloud aria-hidden="true" />
                  <span>
                    {logoFile
                      ? logoFile.name
                      : "Ajoutez votre logo si vous en avez déjà un (5 Mo max.)"}
                  </span>
                </label>
                <input
                  id="identity-logo"
                  type="file"
                  accept="image/*,.pdf"
                  className="visually-hidden"
                  onChange={handleLogoChange}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {step === "choice" && <GuideSection {...visualIdentityGuide} />}

      <SummaryBar
        label={label}
        total={selected.length === 0 ? "—" : formatPrice(total)}
        buttonLabel={
          submitting
            ? "Enregistrement…"
            : step === "choice"
              ? "Continuer"
              : "Continuer vers le paiement"
        }
        disabled={submitting || selected.length === 0}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default VisualIdentity;
